import React from 'react';
import { getLatestMessage } from '../../api/cmail.api';
import useNotifications from '../../_lib/notifications/useNotifications';
import { CmailModal } from './CmailModal';

const MODAL_CLOSE_ANIMATION_DELAY_MS = 300;

interface CmailModalContextValue {
  openCmailModal: (msgId: string) => Promise<void>;
  closeCmailModal: () => void;
  isOpen: boolean;
}

const CmailModalContext = React.createContext<CmailModalContextValue | null>(
  null
);

export const useCmailModal = (): CmailModalContextValue => {
  const context = React.useContext(CmailModalContext);
  if (!context) {
    throw new Error('useCmailModal must be used within a CmailModalProvider');
  }
  return context;
};

const getLatestMessageId = async (msgId: string): Promise<string> => {
  try {
    const response = await getLatestMessage({ params: { msgId } });
    const messageData = response.data || response;
    if (messageData?.msgId) {
      return messageData.msgId;
    }
    console.warn(
      'No msgId found in latest message response, using original msgId:',
      {
        originalMsgId: msgId,
        responseData: messageData,
      }
    );
    return msgId;
  } catch (error) {
    console.error('Error fetching latest message:', error);
    throw error;
  }
};

interface CmailModalProviderProps {
  children: React.ReactNode;
}

export const CmailModalProvider: React.FC<CmailModalProviderProps> = ({
  children,
}) => {
  const notifications = useNotifications();
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentMsgId, setCurrentMsgId] = React.useState<string | null>(null);

  const openCmailModal = React.useCallback(
    async (msgId: string) => {
      try {
        const latestMsgId = await getLatestMessageId(msgId);
        setCurrentMsgId(latestMsgId);
        setIsOpen(true);
      } catch (error) {
        notifications.error(
          'Failed to fetch latest email message. Please try again.'
        );
      }
    },
    [notifications]
  );

  const closeCmailModal = React.useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setCurrentMsgId(null);
    }, MODAL_CLOSE_ANIMATION_DELAY_MS);
  }, []);

  const handleModalClose = React.useCallback(() => {
    closeCmailModal();
  }, [closeCmailModal]);

  const contextValue = React.useMemo(
    () => ({
      openCmailModal,
      closeCmailModal,
      isOpen,
    }),
    [openCmailModal, closeCmailModal, isOpen]
  );

  return (
    <CmailModalContext.Provider value={contextValue}>
      {children}
      {/* Modal is rendered at provider level, outside any accordion/drawer/popover */}
      {currentMsgId && (
        <CmailModal
          open={isOpen}
          onClose={handleModalClose}
          msgId={currentMsgId}
        />
      )}
    </CmailModalContext.Provider>
  );
};
